/**
 * PricingPage
 *
 * Shows both tiers, and describes them from the code rather than from memory.
 *
 * What changed and why:
 *
 *   - There was no free column, so a visitor could not tell what they already had or what they would
 *     gain. Naming the free tier is also the honest thing to do — three real coverages is a usable
 *     product, and saying so builds more trust than implying the app is unusable unpaid.
 *   - Two of the four "Premium" bullets were things free users already get ("Unlimited route
 *     simulations", "Real-time play visualization"). Selling someone a feature they have is the
 *     fastest way to lose them at the point of purchase.
 *   - "Build custom playbooks" was not the name of any feature in the product. It is the Defense
 *     Play Maker, and Save Play.
 *
 * The lists come from utils/premiumFeatures, which also holds the arrays the paywall itself checks —
 * so this page cannot describe a gate that does not exist.
 */

import { Link } from 'react-router-dom';
import { freeFeatures, premiumFeatures } from '../utils';
import './PricingPage.css';

function PricingPage() {
  return (
    <div className="pricing-page">
      <section className="pricing-hero">
        <h1>Simple pricing</h1>
        <p className="pricing-subtitle">
          Start free with three coverages. Upgrade when you want the match schemes, trips, and a
          playbook you can keep.
        </p>
      </section>

      <section className="pricing-content">
        <div className="pricing-card pricing-card-free">
          <div className="pricing-card-header">
            <h2>Free</h2>
            <div className="pricing-amount">
              <span className="price">$0</span>
            </div>
            <p className="pricing-card-note">No account needed</p>
          </div>
          <ul className="pricing-features">
            {freeFeatures().map((feature) => (
              <li key={feature}>
                <span className="feature-icon" aria-hidden="true">✓</span>
                {feature}
              </li>
            ))}
          </ul>
          <Link to="/simulator" className="pricing-cta-button pricing-cta-secondary">
            Open the Simulator
          </Link>
        </div>

        <div className="pricing-card pricing-card-premium">
          <div className="pricing-card-header">
            <span className="pricing-card-flag">Everything in Free, plus</span>
            <h2>Premium</h2>
            <div className="pricing-amount">
              <span className="price">$7.99</span>
              <span className="period">/month</span>
            </div>
            <p className="pricing-card-note">Cancel any time</p>
          </div>
          <ul className="pricing-features">
            {premiumFeatures().map((feature) => (
              <li key={feature}>
                <span className="feature-icon" aria-hidden="true">✓</span>
                {feature}
              </li>
            ))}
          </ul>
          <Link to="/auth" className="pricing-cta-button">
            Get Premium
          </Link>
        </div>
      </section>

      <section className="pricing-faq">
        <h2>Questions?</h2>
        <p>Have questions about our pricing or features? We're here to help.</p>
        <Link to="/contact" className="contact-link-button">
          Contact Us
        </Link>
      </section>
    </div>
  );
}

export default PricingPage;
